import * as React from 'react';
import { QueryRenderer, graphql, commitMutation } from 'react-relay';
import { Moment } from 'moment';
import environment from 'api/environment';
import { GraphError } from 'types/general';
import { useToast } from 'components/Toasts/ToastProvider';
import RequestBackgroundCheckModal, {
  CheckType
} from './RequestBackgroundCheckModal/RequestBackgroundCheckModal';
import { RequestBackgroundCheckLoaderQueryResponse } from './__generated__/RequestBackgroundCheckLoaderQuery.graphql';
import { RequestBackgroundCheckLoaderMutationResponse } from './__generated__/RequestBackgroundCheckLoaderMutation.graphql';

// Loads the company pricing/quotas the request modal needs and wires the
// mutation, so a background check can be requested outside a delegate profile
// page (e.g. from the Background Checks list). The delegate is chosen inside
// the modal's first stage, so none is preselected here.

const dataQuery = graphql`
  query RequestBackgroundCheckLoaderQuery {
    manager {
      firstName
      lastName
      company {
        name
        backgroundCheckPennyPrice
        dbsPennyPrice
        dbsOnlyPennyPrice
        renewalPennyPrice
        invoicing {
          dbsUsed
          dbsPrePurchased
          backgroundChecksUsed
          backgroundChecksPrePurchased
          renewalsUsed
          renewalsPrePurchased
        }
      }
    }
  }
`;

const requestMutation = graphql`
  mutation RequestBackgroundCheckLoaderMutation(
    $delegateUUID: UUID!
    $requestDBS: Boolean!
    $isRenewal: Boolean
    $onlyDBS: Boolean
    $idExpiry: Time
    $isCAA: Boolean
  ) {
    requestBackgroundCheck(
      delegateUUID: $delegateUUID
      requestDBS: $requestDBS
      isRenewal: $isRenewal
      onlyDBS: $onlyDBS
      idExpiryDate: $idExpiry
      isCAA: $isCAA
    )
  }
`;

type Props = {
  onClose: () => void;
  onCompleted?: () => void;
};

function RequestBackgroundCheckLoader({ onClose, onCompleted }: Props) {
  const toast = useToast();

  const requestBackgroundCheck = (
    delegateUUID: string,
    requestDBS: boolean,
    checkType: CheckType,
    idExpiry?: Moment
  ) => {
    commitMutation(environment, {
      mutation: requestMutation,
      variables: {
        delegateUUID,
        requestDBS,
        isRenewal: checkType === 'renewal',
        onlyDBS: checkType === 'onlyDBS',
        idExpiry,
        isCAA: checkType === 'onlyDBS' ? false : true
      },
      onCompleted: (
        response: RequestBackgroundCheckLoaderMutationResponse,
        errors: GraphError[]
      ) => {
        if (errors) {
          toast({
            message: {
              variant: 'error',
              text: errors?.[0]?.extensions?.helpText ?? errors?.[0]?.message
            }
          });
          return;
        }
        if (response.requestBackgroundCheck) {
          toast({
            message: { variant: 'success', text: 'Background check requested' }
          });
          onCompleted && onCompleted();
        }
      },
      onError: (err: any) => {
        toast({
          message: {
            variant: 'error',
            text:
              err?.[0]?.extensions?.helpText ?? err?.[0]?.message ?? err.message
          }
        });
      }
    });
  };

  return (
    <QueryRenderer
      environment={environment}
      query={dataQuery}
      variables={{}}
      render={({ props }) => {
        const data = props as RequestBackgroundCheckLoaderQueryResponse | null;
        const manager = data?.manager;
        const company = manager?.company;
        if (!manager || !company) return null;

        const invoicing = company.invoicing;
        return (
          <RequestBackgroundCheckModal
            isOpen
            companyName={company.name}
            managerName={`${manager.firstName} ${manager.lastName}`}
            renewalCost={company.renewalPennyPrice}
            checkCost={company.backgroundCheckPennyPrice}
            dbsCost={company.dbsPennyPrice}
            dbsOnlyCost={company.dbsOnlyPennyPrice}
            checksLeft={
              (invoicing?.backgroundChecksPrePurchased ?? 0) -
              (invoicing?.backgroundChecksUsed ?? 0)
            }
            dbsLeft={
              (invoicing?.dbsPrePurchased ?? 0) - (invoicing?.dbsUsed ?? 0)
            }
            renewalsLeft={
              (invoicing?.renewalsPrePurchased ?? 0) -
              (invoicing?.renewalsUsed ?? 0)
            }
            onRequest={(delegateUUID, requestDBS, checkType, idDate) => {
              requestBackgroundCheck(
                delegateUUID,
                requestDBS,
                checkType,
                idDate
              );
              onClose();
            }}
            onClose={onClose}
          />
        );
      }}
    />
  );
}

export default RequestBackgroundCheckLoader;
